/**
 * AssetLedger Pro — Auth Store (Zustand)
 *
 * Global authentication state with session timeout tracking.
 * Access token stored in memory only (not localStorage) for security.
 * Refresh token handled as httpOnly cookie by the server.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: string;
  clientId: string | null;
  twoFactorEnabled: boolean;
  avatarUrl: string | null;
  client?: {
    id: string;
    name: string;
    shortCode: string;
    status: string;
  } | null;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;    // In-memory only — never persisted
  isAuthenticated: boolean;
  isLoading: boolean;
  lastActivityAt: number | null;

  // Actions
  setUser: (user: AuthUser) => void;
  setAccessToken: (token: string) => void;
  login: (user: AuthUser, accessToken: string) => void;
  logout: () => void;
  updateLastActivity: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,       // Never persisted (see partialize below)
      isAuthenticated: false,
      isLoading: true,
      lastActivityAt: null,

      setUser: (user) => set({ user }),

      setAccessToken: (token) => set({ accessToken: token }),

      login: (user, accessToken) =>
        set({
          user,
          accessToken,
          isAuthenticated: true,
          isLoading: false,
          lastActivityAt: Date.now(),
        }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          isLoading: false,
          lastActivityAt: null,
        }),

      updateLastActivity: () => set({ lastActivityAt: Date.now() }),

      setLoading: (loading) => set({ isLoading: loading }),
    }),
    {
      name: 'alp-auth',
      storage: createJSONStorage(() => sessionStorage), // sessionStorage — cleared on tab close
      // IMPORTANT: Never persist the access token
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        lastActivityAt: state.lastActivityAt,
        // accessToken intentionally excluded
      }),
    }
  )
);
